import React, {useState, useEffect, useMemo, useCallback} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Modal from 'react-modal';
import ReviewStatus from '../../../config/ReviewStatus.js';
import { DateUtils } from '../../../utils/DateUtils.js';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js'
import { AutoAwesome, Settings, PlayCircleFilled, CheckCircle, ViewColumn } from '@mui/icons-material';
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader';
import TableFilterConfig from '../../../uiComponents/MuiTable/TableFilterConfig.js';
import MuiTablePagination from '../../../uiComponents/MuiTable/MuiTablePagination';
import {stableSort, getComparator} from '../../../uiComponents/MuiTable/MuiHelper';
import { 
  TableBody,
  TableContainer,
  Button,
  Tooltip
} from '@mui/material';
import './index.css';
import SessionConstants from '../../../config/SessionConstants.js';
import APIUtils from '../../../utils/APIUtils.js';
import VariantSettingsModal from '../../VariantDashboard/VariantSettingsModal/index.js';
import Loading from '../../Loading/index.js';
import makeStyles from '@mui/styles/makeStyles';
import SelectFieldsDialog from './columnOptions.js';
import { CommaBreakText, DbSnpLink, GeneCell, VariantDetailsLink } from '../../../uiComponents/VariantCell.js';
import { QUICK_ACTION_LABELS, quickActionsFilterConfig, withQuickActions } from '../../../utils/QuickActions.js';
import QuickActionButtons from '../../../uiComponents/QuickActionButtons.js';
import { loadViewState, saveTableViewState, VIEW_SCOPE } from '../../../utils/ProgramSummaryViewState.js';

const useStyles = makeStyles({
  // Right-aligned Settings action; hugs its content.
  settingsButton: {
    padding: '5px 10px',
    lineHeight: 1.2,
    '& .MuiButton-startIcon': {
      marginTop: '-1px',
    },
  },
});

const fixedColumnIds = ['newReview', 'status', 'reviewSettings'];

const defaultColumnIds = [
  'chromosomalHGVS',
  'interpretationStatusString',
  'currentReviewVersion',
  'numPatientsIdentified',
  'gene',
  'potentialCondition',
  'programClassification',
  'createdDate',
  'closedDate',
];

//Important: The id here must match the column name in the returned data. Label can be different for display.
const availableHeadCells = [
  { label: 'Chromosome Reference', id: 'chromosomeReference', numeric: false, disablePadding: false },
  { label: 'Chromosomal HGVS', id: 'chromosomalHGVS', numeric: false, disablePadding: false },
  { label: 'Transcript Reference HGVS', id: 'transcriptReferenceHGVS', numeric: false, disablePadding: false },
  { label: 'Clinvar Variant ID', id: 'clinvarVariantId', numeric: false, disablePadding: false },
  { label: 'New', id: 'newReview', align: 'center', disablePadding: false },
  { label: 'Quick Actions', id: 'status', disablePadding: false },
  { label: 'Latest Review Status', id: 'interpretationStatusString', numeric: false, disablePadding: false },     
  { label: 'Review Version', id: 'currentReviewVersion', numeric: true, disablePadding: false, align: 'left' },
  { label: 'Patients With Variant', id: 'numPatientsIdentified', numeric: false, disablePadding: false },
  { label: 'Gene', id: 'gene', numeric: false, disablePadding: false },
  { label: 'Condition', id: 'potentialCondition', numeric: false, disablePadding: false },
  { label: 'Program Classification', id: 'programClassification', numeric: false, disablePadding: false },
  { label: 'Date Uploaded', id: 'createdDate', numeric: false, disablePadding: false },
  { label: 'Last Reviewed', id: 'closedDate', numeric: false, disablePadding: false },
  { label: 'Date Last Identified', id: 'dateLastIdentified', numeric: false, disablePadding: false },
  { label: 'dbSNP RS Number', id: 'dbSnpRsNumber', numeric: false, disablePadding: false },
  { label: 'Review Settings', id: 'reviewSettings', type: 'noSort', align: 'right', disablePadding: false },
];

const headCellById = availableHeadCells.reduce((lookup, column) => {
  lookup[column.id] = column;
  return lookup;
}, {});

const columnWidths = {
  newReview: '5%',
  status: '12%',
  chromosomalHGVS: '18%',
  interpretationStatusString: '20%',
  currentReviewVersion: '2%',
  numPatientsIdentified: '2%',
  gene: '7%',
  potentialCondition: '7%',
  programClassification: '8%',
  createdDate: '10%',
  closedDate: '10%',
  clinvarVariantId: '8%',
  transcriptReferenceHGVS: '18%',
  dateLastIdentified: '10%',
  dbSnpRsNumber: '8%',
  patientsWithVariant: '8%',
  reviewSettings: '2%',
};

const columnCellClassNames = {
  gene: 'gene',
};

const columnCellStyleOverrides = {
  newReview: { verticalAlign: 'middle', textAlign: 'center' },
  status: { verticalAlign: 'middle' },
  reviewSettings: { textAlign: 'right' },
  chromosomalHGVS: { wordBreak: 'break-word' },
  transcriptReferenceHGVS: { wordBreak: 'break-word' },
};

const getColumnLabel = (column) => column.label || column.hiddenHeader || column.id;

const normalizeColumnKey = (value) => String(value || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

const columnAliases = availableHeadCells.reduce((lookup, column) => {
  [column.id, column.label, column.hiddenHeader].forEach((value) => {
    if (value) {
      lookup[normalizeColumnKey(value)] = column.id;
    }
  });
  return lookup;
}, {});

const getColumnId = (column) => {
  const value = typeof column === 'string'
    ? column
    : column?.id || column?.columnId || column?.field || column?.fieldName || column?.name || column?.value || column?.label || column?.displayName;

  return columnAliases[normalizeColumnKey(value)];
};

const getApiColumnLabel = (apiColumn, columnId) => {
  if (typeof apiColumn === 'object') {
    return apiColumn.label || apiColumn.displayName || apiColumn.header || apiColumn.name || getColumnLabel(headCellById[columnId]);
  }

  return getColumnLabel(headCellById[columnId]);
};

const getApiColumnEnabled = (apiColumn) => {
  if (typeof apiColumn !== 'object') {
    return true;
  }

  return apiColumn.enabled ?? apiColumn.visible ?? apiColumn.isVisible ?? true;
};

const getDefaultColumnFields = () => availableHeadCells
  .filter((column) => defaultColumnIds.includes(column.id))
  .map((column) => ({
    id: column.id,
    label: getColumnLabel(column),
    enabled: true,
  }));

const getAvailableColumnFields = () => availableHeadCells
  .filter((column) => !fixedColumnIds.includes(column.id))
  .map((column) => ({
    id: column.id,
    label: getColumnLabel(column),
    enabled: true,
  }));

const createColumnFields = (apiColumns) => {
  if (!Array.isArray(apiColumns) || apiColumns.length === 0) {
    return [];
  }

  const columnIds = new Set();
  const fields = apiColumns.reduce((columns, apiColumn) => {
    const columnId = getColumnId(apiColumn);

    if (!columnId || fixedColumnIds.includes(columnId) || columnIds.has(columnId)) {
      return columns;
    }

    columnIds.add(columnId);
    columns.push({
      id: columnId,
      label: getApiColumnLabel(apiColumn, columnId),
      enabled: getApiColumnEnabled(apiColumn),
    });

    return columns;
  }, []);

  return fields;
};

const getColumnsFromPreferenceResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response) {
    return null;
  }

  return response.userColumns || response.defaultColumns || response.columns || null;
};

const addFixedColumns = (columns) => {
  const nextColumns = columns.filter((column) => !fixedColumnIds.includes(column.id));
  const statusColumn = {
    id: 'status',
    label: getColumnLabel(headCellById.status),
    enabled: true,
  };

  nextColumns.unshift(statusColumn);
  nextColumns.unshift({
    id: 'newReview',
    label: getColumnLabel(headCellById.newReview),
    enabled: true,
  });
  nextColumns.push({
    id: 'reviewSettings',
    label: getColumnLabel(headCellById.reviewSettings),
    enabled: true,
  });

  return nextColumns;
};

export default function VariantsTable(props) {
  const classes = useStyles();
  // Filter/sort/pagination saved when the user last drilled in from this program.
  const savedView = useMemo(() => loadViewState(VIEW_SCOPE.programSummary, props.programId).variants || {}, [props.programId]);
  const [rows, setRows] = useState(props.data); //store initial table data to reset filtering, does not affect assigned to me data since no filtering on that
  const [tableData, setTableData] = useState(props.data);
  const [order, setOrder] = useState(savedView.order || 'desc'); //default sort direction
  const [orderBy, setOrderBy] = useState(savedView.orderBy || 'createdDate'); //default sort column for table
  const [page, setPage] = useState(savedView.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(savedView.rowsPerPage || 10);
  const [modalPatientsIsOpen, setModalPatientsIsOpen] = useState(false);
  const [modalVariantName, setModalVariantName] = useState(null);
  const [modalPatientsList, setModalPatientsList] = useState([]);
  const [modalPotentialCondition, setModalPotentialCondition] = useState(null);
  const [focus, setFocus] = useState('');
  const [variantReview, setVariantReview] = useState(null);
  const [interpreterData, setInterpreterData] = useState([]);
  const [childComponentKey, setChildComponentKey] = useState(0);
  const [variantSettingsModalIsOpen, setVariantSettingsModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [columnsModalIsOpen, setColumnsModalIsOpen] = useState(false);
  const [columnFields, setColumnFields] = useState(() => createColumnFields());
  const [columnsLoaded, setColumnsLoaded] = useState(false);
  // Current user's interpretations keyed by variantId; supplies assigned status + ids.
  const [assignedByVariantId, setAssignedByVariantId] = useState({});
  const [assignedLoaded, setAssignedLoaded] = useState(false);
  // Local date key (YYYY-M-D); flips at midnight so "uploaded today" derivations recompute.
  const [todayKey, setTodayKey] = useState(() => new Date().toDateString());
  const defaultColumnFields = getDefaultColumnFields();
  const availableColumnFields = getAvailableColumnFields();
  const displayedColumnFields = columnFields.length > 0 ? columnFields : defaultColumnFields;

  const ready = columnsLoaded && assignedLoaded;

  const visibleColumnIds = addFixedColumns(displayedColumnFields)
    .filter((column) => column.enabled)
    .map((column) => column.id);
  const visibleHeadCells = visibleColumnIds.map((columnId) => headCellById[columnId]).filter(Boolean);
  const tableRenderKey = `${JSON.stringify(tableData)}-${visibleColumnIds.join('|')}`;

  //intialize tableData
  useEffect(() => {
    // Keep the unfiltered source rows in sync with parent-driven data refreshes.
    // tableData is driven solely by TableFilterConfig (onFilteredDataChange) so a
    // restored/active filter isn't overwritten with the full dataset on mount/refresh.
    setRows(props.data);
    // console.log("table data: " + JSON.stringify(props.data));
  }, [props.data] );

  useEffect(() => {
    // Roll todayKey over at the next local midnight so Quick Action(s) recompute
    // if the table stays mounted across a day boundary, then reschedule.
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timerId = setTimeout(() => {
      setTodayKey(new Date().toDateString());
    }, nextMidnight.getTime() - now.getTime());

    return () => clearTimeout(timerId);
  }, [todayKey]);

  useEffect(() => {
    const loadColumnPreferences = async () => {
      try {
        const columnPreferenceResponse = await APIUtils.getVariantSummaryUserColumns();
        const columns = getColumnsFromPreferenceResponse(columnPreferenceResponse);
        setColumnFields(createColumnFields(columns));
      } catch (error) {
        console.error('Error getting variant summary user columns');
      } finally {
        setColumnsLoaded(true);
      }
    };

    if (sessionStorage.getItem(SessionConstants.USER_EMAIL)) {
      loadColumnPreferences();
    } else {
      setColumnsLoaded(true);
    }
  }, []);

  const loadAssignedInterpretations = useCallback(async () => {
    if (!sessionStorage.getItem(SessionConstants.USER_EMAIL)) {
      setAssignedLoaded(true);
      return;
    }
    try {
      const assigned = await APIUtils.getInterpretationsAssignedToUser();
      const lookup = (Array.isArray(assigned) ? assigned : []).reduce((map, item) => {
        const variantId = item?.variant?.variantObjectId;
        if (variantId) {
          map[variantId] = {
            status: item.interpretation?.interpretationStatus,
            variantReviewId: item.interpretation?.variantReviewId,
            variantInterpretationId: item.interpretation?.variantInterpretationId,
          };
        }
        return map;
      }, {});
      setAssignedByVariantId(lookup);
    } catch (error) {
      console.error('Error getting interpretations assigned to user');
    } finally {
      setAssignedLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadAssignedInterpretations();
  }, [loadAssignedInterpretations]);

  // Persist the current sort/pagination so a return to Program Summary restores it.
  useEffect(() => {
    saveTableViewState(VIEW_SCOPE.programSummary, props.programId, 'variants', { order, orderBy, page, rowsPerPage });
  }, [props.programId, order, orderBy, page, rowsPerPage]);

  // Keep a restored/active page valid: clamp to the last page when filtered data shrinks.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(tableData.length / rowsPerPage) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [tableData, rowsPerPage, page]);

  useEffect(() => {
    //if upload was successful, sort by date desc
    if (props.sortTableByDateDesc) {
      setOrder('desc');
      setOrderBy('createdDate');
    }
  }, [props.sortTableByDateDesc]);

  const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      setPage(0);
  };
  
  const handleChangePage = (event, newPage) => {
      setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
  };

  const configColumns = () => {
    setColumnsModalIsOpen(true);
  };

  const closeColumnsModal = () => {
    setColumnsModalIsOpen(false);
  };

  const handleSaveColumns = async (fields) => {
    const selectedFields = Array.isArray(fields) && fields.length > 0 ? fields : [defaultColumnFields[0]];
    const nextFields = selectedFields.filter((field) => field?.id);
    const nextVisibleColumnIds = nextFields.map((field) => String(field.id));

    await APIUtils.saveVariantSummaryUserColumns(nextVisibleColumnIds);

    if (!nextVisibleColumnIds.includes(orderBy)) {
      setOrderBy(nextVisibleColumnIds[0]);
    }

    setColumnFields(nextFields);
  };

  //Check if variant was uploaded today with status REVIEW_CREATED
  const isUploadedToday = (row) => {
    if (row.reviewStatus === ReviewStatus.REVIEW_CREATED) {
      const date = new Date(row.createdDate);
      const currentDate = new Date();
      return date.getFullYear() === currentDate.getFullYear() &&
        date.getMonth() === currentDate.getMonth() &&
        date.getDate() === currentDate.getDate();
    }
    return false;
  };

  // Derive the Quick Action labels shown for a row so they can drive the filter options.
  const getQuickActionsForRow = (row) => {
    const actions = [];
    const assignment = assignedByVariantId[row.variantId];
    if (assignment && assignment.status !== 'Completed') {
      actions.push(QUICK_ACTION_LABELS.assigned);
    }
    if (row.reviewStatus === ReviewStatus.CLOSED) {
      actions.push(QUICK_ACTION_LABELS.consensusComplete);
    } else if (row.reviewStatus === ReviewStatus.READY_FOR_CONSENSUS) {
      actions.push(QUICK_ACTION_LABELS.readyForConsensus);
    } else if (row.reviewStatus === ReviewStatus.CONSENSUS_IN_PROGRESS) {
      actions.push(QUICK_ACTION_LABELS.consensusInProgress);
    }
    return actions;
  };

  // Augment rows with a synthetic quickActions field for the Quick Action(s) filter,
  // plus a boolean newReview field so the New column can be sorted.
  const rowsWithQuickActions = useMemo(
    () => withQuickActions(rows, getQuickActionsForRow).map((row) => ({ ...row, newReview: isUploadedToday(row) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, assignedByVariantId, todayKey]
  );

  // Standalone "New" indicator rendered in its own column for reviews opened today.
  const newReviewCell = (row) => {
    if (!isUploadedToday(row)) {
      return null;
    }
    return (
      <Tooltip title="New review" placement="top" arrow>
        <span className="newBadge" tabIndex={0} aria-label="New review">
          <AutoAwesome aria-hidden="true" className="tableIcon" style={{ color: 'inherit', fontSize: '0.875rem', marginTop: '-5px' }} /> New
        </span>
      </Tooltip>
    );
  };

  const statusCell = (row) => {
    let linkText = "";
    let IconComponent = null;
    switch (row.reviewStatus) {
      case ReviewStatus.CLOSED:
        linkText = "Consensus review complete";
        IconComponent = CheckCircle;
        break;
      case ReviewStatus.READY_FOR_CONSENSUS:
      case ReviewStatus.CONSENSUS_IN_PROGRESS:
        linkText = row.reviewStatus === ReviewStatus.READY_FOR_CONSENSUS
          ? "Ready for consensus review"
          : "Consensus review in progress";
        IconComponent = PlayCircleFilled;
        break;
      case ReviewStatus.DELETED:
        return "Deleted";
      default:
        return <CommaBreakText value={row.interpretationStatusString} />;
    }
    return (
      <RouterLink
        to={`/variantconsensus/${row.variantId}`}
        state={{
          variantId: row.variantId,
            reviewStatus: row.reviewStatus,
            variantName: row.variantName,
            gene: row.gene,
            potentialCondition: row.potentialCondition,
            breadCrumbs: props.breadCrumbs,
        }}
      >
        {linkText} {IconComponent && <IconComponent style={{ verticalAlign: 'middle', fontSize: '1rem' }} />}
      </RouterLink>
    );
  };
  

  const reviewSettingsCell = (row) => {
    // show variant review settings button if no consensus reviews and if role is program admin
    if ((row.reviewStatus === ReviewStatus.IN_PROGRESS || row.reviewStatus === ReviewStatus.REVIEW_CREATED) && 
        sessionStorage.getItem(SessionConstants.IS_MANAGER)==='true') {
      return (
        <Button
          className={classes.settingsButton}
          variant="outlined"
          color="primary"
          aria-label="Variant Review Settings" 
          onClick={() => handleLoadDataAndOpenModal(row.variantId)}
          startIcon={<Settings fontSize='small' />}
        >
          Settings
        </Button>
      )
    }
  }

  //open patients modal
  const handlePatientsClick = (event, variantName, patientsList, variantId, potentialCondition) => {
    event.preventDefault();
    setModalPatientsIsOpen(true);
    setModalVariantName(variantName);
    setModalPatientsList(patientsList);
    setFocus(variantId);
    setModalPotentialCondition(potentialCondition);
  }
  //keyboard accessibility to open patients modal
  const handlePatientsKey = (event, variantName, patientsList, variantId, potentialCondition) => {
    if (event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault();
      setModalPatientsIsOpen(true);
      setModalVariantName(variantName);
      setModalPatientsList(patientsList);
      setFocus(variantId);
      setModalPotentialCondition(potentialCondition);
    }
  }

  const patientsCell = (row) => {
    return (
      <a
        id={row.variantId} //set id for focus return on modal close
        href='#'
        tabIndex="0"
        onClick={(event) => {handlePatientsClick(event, row.variantName, row.patientsList, row.variantId, row.potentialCondition)}}
        onKeyDown={(event) => {handlePatientsKey(event, row.variantName, row.patientsList, row.variantId, row.potentialCondition)}}
      >
        {row.patientsList.length}
      </a>
    )
  }

  //load patients list for modal
  const patientsList = () => {
    return (
      modalPatientsList ? 
      (
        <div>
          {modalPatientsList.map((item, index) =>(
              <div className="patientListItem" key={index}>
                <RouterLink
                  to={`/patientdetails/${item}`}
                  state={{
                    programId: props.programId,
                    breadCrumbs: props.breadCrumbs,
                    potentialConditionsString: modalPotentialCondition,
                  }}
                >
                  {item}
                </RouterLink>
              </div>
            ))}
        </div>
      ) :
      (
        <div>No Patients Found</div>
      )
    )
  }

  const closeModal = () => {
    setModalPatientsIsOpen(false);
  };

  const returnFocus = () => {
    document.getElementById(focus).focus();
  };

  // Load data function
  const loadData = async (variantId) => {
    const programId = props.programId;
    const variantResponse = await APIUtils.getReview(programId, variantId); //UPDATE TO VARIANT ID
    const variantReview = variantResponse;
    const newChildComponentKey = childComponentKey + 1;
    const interpretations = await APIUtils.getInterpretationsByReview(variantReview.variantReviewId);
    const modifiedInterpreterData = interpretations.map(item => ({ // modify response for table column sorting
        ...item,
        completed: item.interpretationStatus === 'Completed',
        inProgress: item.interpretationStatus !== 'Completed'
    }));
    setVariantReview(variantReview);
    setInterpreterData(modifiedInterpreterData);
    setChildComponentKey(newChildComponentKey);
  };

  // Close modal
  const closeVariantSettingsModal = () => {
    setVariantSettingsModalIsOpen(false);
  };

  // Refresh function
  const refresh = async () => {
    //console.log("---refreshing data---")
    setIsLoading(true);
    // Refresh variant rows and the current user's assigned-interpretations lookup together
    // so Quick Actions (e.g. "Assigned to Me") reflect Settings-modal changes immediately.
    try {
      const [variants] = await Promise.all([
        APIUtils.getVariantsByProgramId(props.programId),
        loadAssignedInterpretations(),
      ]);
      if (variants) {
        setRows(variants); //refresh the init rows table data; TableFilterConfig will re-apply any active filter via its rows useEffect
      }
    } catch (error) {
      console.error('Error getting variants')
    }
    setIsLoading(false);
    // console.log("---refreshed table data: " + JSON.stringify(variants))
  };

  const handleLoadDataAndOpenModal = async (variantId) => {
    //setUserValues([]);
    setIsLoading(true);
    setVariantReview(null);
    // Set the modal to open immediately
    setVariantSettingsModalIsOpen(true);
      // Trigger data loading without awaiting it
    await loadData(variantId);
  };

  const columnCellRenderers = {
    newReview: (row) => newReviewCell(row),
    status: (row) => (
      <QuickActionButtons
        row={row}
        assignment={assignedByVariantId[row.variantId]}
        breadCrumbs={props.breadCrumbs}
        programId={props.programId}
        assignedNavExtras={{ fromProgramSummary: true }}
      />
    ),
    chromosomalHGVS: (row) => <VariantDetailsLink value={row.chromosomalHGVS} row={row} breadCrumbs={props.breadCrumbs} />,
    transcriptReferenceHGVS: (row) => <VariantDetailsLink value={row.transcriptReferenceHGVS} row={row} breadCrumbs={props.breadCrumbs} />,
    interpretationStatusString: (row) => statusCell(row),
    currentReviewVersion: (row) => row.currentReviewVersion,
    numPatientsIdentified: (row) => patientsCell(row),
    gene: (row) => <GeneCell gene={row.gene} />,
    createdDate: (row) => DateUtils.FormatLocalDate(row.createdDate),
    closedDate: (row) => DateUtils.FormatLocalDate(row.closedDate),
    dateLastIdentified: (row) => DateUtils.FormatLocalDate(row.dateLastIdentified),
    dbSnpRsNumber: (row) => <DbSnpLink rsNumber={row.dbSnpRsNumber} />,
    reviewSettings: (row) => reviewSettingsCell(row),
  };

  const renderColumnCell = (columnId, row) => {
    const renderCellContent = columnCellRenderers[columnId];
    const cellStyle = {
      width: columnWidths[columnId],
      ...columnCellStyleOverrides[columnId],
    };

    return (
      <StyledTableCell
        key={columnId}
        className={columnCellClassNames[columnId]}
        style={cellStyle}
      >
        {renderCellContent ? renderCellContent(row) : row[columnId]}
      </StyledTableCell>
    );
  };

  return (
    <>
      <Loading isLoading={isLoading || !ready}/>
      {!ready && <div className="noDataLarge"></div>}
      {ready && (
        <>
          <div className="tableFilterGrid">
            <TableFilterConfig 
              rows={rowsWithQuickActions}
              initialSearchFilter={savedView.searchFilter || ''}
              initialFilterBy={savedView.filterBy || ''}
              onFilterCriteriaChange={(type, value) => {
                saveTableViewState(VIEW_SCOPE.programSummary, props.programId, 'variants', { searchFilter: type, filterBy: value });
                setPage(0);
              }}
              onFilteredDataChange={(filtered) => {
                setTableData(filtered);
              }}
              filterConfig={[
                { type: 'gene', label: 'Gene(s)', field: 'gene' },
                { type: 'condition', label: 'Condition(s)', field: 'potentialCondition' },
                quickActionsFilterConfig,
              ]}
              actions={(
                <Button variant="outlined" onClick={configColumns} aria-label="Column Options" startIcon={<ViewColumn fontSize='small' />} sx={{ whiteSpace: 'nowrap' }}>Column Options</Button>
              )}
            />
          </div>
          <div>
            <StyledPaper>
              <TableContainer>
                <StyledTable
                  size='small'
                  aria-label="variants table"
                  key={tableRenderKey} >
                  <MuiTableHeader 
                    headCells={visibleHeadCells} 
                    order={order}  
                    orderBy={orderBy} 
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {stableSort(tableData, getComparator(order, orderBy))
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                      return (
                        <StyledTableRow tabIndex={-1} key={row.variantId}>
                          {visibleColumnIds.map((columnId) => renderColumnCell(columnId, row))}
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </StyledTable>
              </TableContainer>
              <MuiTablePagination 
                rowsPerPage={rowsPerPage}
                page={page}
                count={tableData.length}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </StyledPaper>
          </div>
        </>
      )}
      <Modal
        className="modal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel="Patients for Variant"
        shouldCloseOnOverlayClick={true}
        isOpen={modalPatientsIsOpen}
        onRequestClose={closeModal}
        onAfterClose={returnFocus}
      >
        <div>
          <div className="modalHeader">Patients for Variant: {modalVariantName}</div>
          <div className="modalContent center">
            {patientsList()}
          </div>
            <div className="modalButtons">
              <button
                className="modalButton"
                onClick={closeModal}
              >
                Close
              </button>
            </div>
        </div>
      </Modal>
      <SelectFieldsDialog
        open={columnsModalIsOpen}
        onClose={closeColumnsModal}
        fields={columnFields}
        availableFields={availableColumnFields}
        defaultFields={defaultColumnFields}
        onSave={handleSaveColumns}
      />
      {variantReview && (
        <VariantSettingsModal
          closeModal={closeVariantSettingsModal}
          modalIsOpen={variantSettingsModalIsOpen}
          refresh={refresh}
          reviewData={variantReview}
          interpretationData={interpreterData}
          key={childComponentKey}
          onReady={() => setIsLoading(false)}
        />
      )}
    </>
  );
}
